import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { Contents, EpubCFI, Rendition } from 'epubjs';
import { useTheme } from 'vuetify';

import type { Highlight, NewHighlight } from '@ilteducation/annotations-types';
import { useHighlights } from '@ilteducation/annotations';

import useClickHandler from '../composables/use-click-handler';
import { sendBookReaderEventAnalytics } from '../composables/use-send-analytics';
import useReaderStore from './reader';
import useUiStore from './ui';
import { applyCustomAnnotationRects } from '../util/epubJs/epub-dom';
import { cfiRangesOverlap, isCfiRangeContainedIn } from '../util/location';

export type HighlightWithText = Omit<Highlight, 'type'> & { type: 'highlight' | 'underline'; text: string };
export type NewAnnotation = Omit<NewHighlight, 'type'> & { type: 'highlight' | 'underline' };
export type SelectedText = { text: string; cfiRange: string; contents: Contents };
type MarkElMeta = { cfi: string; type: 'highlight' | 'underline'; color: string };

export const USER_HIGHLIGHT_CLASS = 'user-highlight';
export const USER_UNDERLINE_CLASS = 'user-underline';

export const highlightColors = [
  { name: 'pink', light: '#F472B6', dark: '#DB2777' },
  { name: 'yellow', light: '#FDE047', dark: '#CA8A04' },
  { name: 'green', light: '#86EFAC', dark: '#16A34A' },
  { name: 'cyan', light: '#7DD3FC', dark: '#0284C7' },
  { name: 'magenta', light: '#E879F9', dark: '#C026D3' },
  { name: 'red', light: '#DC2626', dark: '#F87171' },
];

const useHighlightsStore = defineStore('highlights', () => {
  const clickHandler = useClickHandler();
  const readerStore = useReaderStore();
  const uiStore = useUiStore();
  const { book, bookId } = storeToRefs(readerStore);
  const theme = useTheme();

  // ProfileId ref that will be set in init()
  const profileIdRef = ref<string | undefined>(undefined);
  const { highlights, load, create, update, remove } = useHighlights(bookId, profileIdRef, { autoLoad: false });

  const isDarkTheme = computed(() => theme.global.current.value.dark);
  const suppressNextSelection = ref(false);
  const highlightsWithText = ref<HighlightWithText[]>([]);
  const selectedText = ref<SelectedText | null>(null);
  const selectionPosition = ref<{ x: number; y: number } | null>(null);
  const lastUsedAnnotation = ref<{ type: 'highlight' | 'underline'; color: string }>({
    type: 'highlight',
    color: 'yellow',
  });

  // Internal state mapping to avoid re-registering already rendered annotations
  const renderedAnnotations = new Map<string, HighlightWithText>();
  let rendition: Rendition | null = null;
  let clickListener: ((highlight: HighlightWithText, position: { x: number; y: number }) => void) | null = null;

  // Safely map raw highlights to HighlightsWithText, avoiding async race conditions.
  watch(
    highlights,
    async (newHighlights, _old, onCleanup) => {
      let isCancelled = false;
      onCleanup(() => {
        isCancelled = false;
      });

      const promises = Object.values(newHighlights)
        .filter(
          (a): a is Highlight => (a.type === 'highlight' || a.type === 'underline') && a.contentId === bookId.value,
        )
        .map(async (a) => {
          const text = (await book.value?.getRange(a.range))?.toString() ?? '';
          return { ...a, type: a.type as 'highlight' | 'underline', text };
        });

      const resolved = await Promise.all(promises);
      if (!isCancelled) highlightsWithText.value = resolved;
    },
    { deep: true },
  );

  // Sync rendered annotations with the state
  watch(
    highlightsWithText,
    (newHighlights) => {
      if (!rendition?.annotations) return;

      const newMap = new Map(newHighlights.map((h) => [h.id, h]));

      // 1. Remove deleted or modified highlights
      for (const [id, oldH] of renderedAnnotations.entries()) {
        const newH = newMap.get(id);
        const hasChanged = newH && (newH.color !== oldH.color || newH.type !== oldH.type || newH.range !== oldH.range);

        if (!newH || hasChanged) teardownAnnotation(oldH);
      }

      // 2. Register new or modified highlights
      for (const [id, newH] of newMap.entries()) {
        if (!renderedAnnotations.has(id)) {
          registerHighlight(newH);
          renderedAnnotations.set(id, { ...newH });
        }
      }
    },
    { deep: true },
  );

  // Re-register highlights when theme changes (blend mode flips)
  watch(isDarkTheme, () => reregisterAllHighlights());

  const getColor = (colorName?: string): string => {
    const defaultYellow = highlightColors.find((c) => c.name === 'yellow')!;
    const baseColor = highlightColors.find((c) => c.name === colorName) || defaultYellow;

    // Check for overridden colors in the Vuetify theme dynamically
    const customThemeColor = theme.global.current.value.colors[`highlight-${baseColor.name}`];

    if (isDarkTheme.value) return baseColor.dark;
    return customThemeColor || baseColor.light;
  };

  const teardownAnnotation = (highlight: Pick<HighlightWithText, 'id' | 'range' | 'type'>) => {
    if (!rendition?.annotations) return;

    rendition.annotations.remove(highlight.range, 'highlight');
    rendition.annotations.remove(highlight.range, 'underline');

    (rendition.getContents() as unknown as Contents[]).forEach((c) => {
      c.document.querySelectorAll(`[data-highlight-cfi="${highlight.range}"]`).forEach((el) => el.remove());
      c.document.querySelector(`[data-annotation-id="${highlight.id}"]`)?.remove();
    });

    clickHandler.unregisterEpubCfiRange(highlight.range);
    renderedAnnotations.delete(highlight.id);
  };

  const registerHighlight = (highlight: HighlightWithText) => {
    if (!rendition || !rendition.annotations) return;

    teardownAnnotation(highlight); // Purge stale DOM nodes first to prevent stacking

    const isUnderline = highlight.type === 'underline' || highlight.color === 'red';
    const className = isUnderline ? USER_UNDERLINE_CLASS : USER_HIGHLIGHT_CLASS;
    const resolvedColor = getColor(highlight.color);

    // Use the correct epub.js type so that remove(cfi, 'underline') / remove(cfi, 'highlight')
    // can find and detach the annotation by its proper internal key.
    const annotationFn = isUnderline ? rendition.annotations.underline : rendition.annotations.highlight;
    const annotation = annotationFn.call(rendition.annotations, highlight.range, {}, undefined, className, {}) as any;

    const markEl = annotation?.mark?.element as SVGGElement | undefined;

    const paintMark = (el: SVGGElement) => {
      // Store metadata on the element so we can redraw it on page turns
      el.dataset.annotationId = highlight.id;
      el.dataset.highlightCfi = highlight.range;
      el.dataset.annotationType = isUnderline ? 'underline' : 'highlight';
      el.dataset.annotationColor = resolvedColor;
      el.style.visibility = 'hidden';

      requestAnimationFrame(() => {
        const activeContents = rendition!.getContents() as unknown as Contents[];
        let painted = false;
        for (const c of activeContents) {
          try {
            const range = c.range(highlight.range);
            if (range) {
              applyCustomAnnotationRects(
                el,
                range,
                isUnderline ? 'underline' : 'highlight',
                resolvedColor,
                isDarkTheme.value,
              );
              painted = true;
              break;
            }
          } catch {
            /* Suppress: CFI not on current page view */
          }
        }
        el.style.visibility = '';

        // Failing to paint leaves the mark with epub.js's default styling instead of the user's color,
        // which looks like a rendering quirk rather than an error.
        // Only warn when the annotation's own section is rendered: "not on this page" is
        // normal, since reregisterAllHighlights() walks every annotation in the book.
        const sections = activeContents.map((c) => c.sectionIndex);
        if (!painted && sections.includes(new EpubCFI(highlight.range).spinePos)) {
          console.warn('[highlights] could not paint annotation on its own section', {
            id: highlight.id,
            range: highlight.range,
            sections,
          });
        }
      });
    };

    if (markEl) paintMark(markEl);

    // epub.js detaches the mark when the section unloads and builds a *fresh* <g> when it renders
    // again, so the metadata and rects painted above are gone. Repaint the element it hands us.
    annotation?.on?.('attach', (mark: any) => {
      if (mark?.element) paintMark(mark.element);
    });

    clickHandler.registerEpubCfiRange(highlight.range, (event) => {
      suppressNextSelectionOnce();
      // Use the iframe the click actually happened in — a spread renders two, and
      // taking getContents()[0] offset right-page clicks by the left page's rect.
      const clickedFrame = (event.view ?? (event.target as Node | null)?.ownerDocument?.defaultView)?.frameElement as
        | HTMLElement
        | null
        | undefined;
      const iframeRect = clickedFrame?.getBoundingClientRect() ?? { left: 0, top: 0 };

      clickListener?.(highlight, { x: iframeRect.left + event.clientX, y: iframeRect.top + event.clientY });
    });
  };

  const reregisterAllHighlights = () => {
    if (!rendition?.annotations) return;
    highlightsWithText.value.forEach((h) => {
      registerHighlight(h);
      renderedAnnotations.set(h.id, { ...h });
    });
  };

  const init = (_bookId: string, _rendition: Rendition, _profileId?: string) => {
    rendition = _rendition;
    profileIdRef.value = _profileId;
    highlightsWithText.value = [];
    renderedAnnotations.clear();
    clearSelectedText();

    // When a click lands on nothing in the iframe (no annotation, no element),
    // dismiss the toolbar if it's visible.
    clickHandler.registerNoMatchCallback(() => {
      if (uiStore.isSelectingText) clearSelectedText();
    });

    _rendition.on('selected', (cfiRange: string, contents: Contents) => {
      if (suppressNextSelection.value) {
        suppressNextSelection.value = false;
        return;
      }

      if (selectedText.value?.cfiRange === cfiRange) return;

      const selection = contents.window.getSelection();
      if (!selection || selection.isCollapsed) return;

      const text = (selection.rangeCount > 0 ? selection.getRangeAt(0).toString() : selection.toString()).trim();
      if (!text) return;

      // Compute position for context menu placement (below + centred on the selection)
      try {
        const selRange = selection.getRangeAt(0);
        const rect = selRange.getBoundingClientRect();
        const iframeEl = contents.document.defaultView?.frameElement as HTMLElement | null;
        const iframeRect = iframeEl?.getBoundingClientRect() ?? { left: 0, top: 0 };

        selectionPosition.value = {
          x: iframeRect.left + rect.left + rect.width / 2,
          y: iframeRect.top + rect.bottom,
        };
      } catch {
        selectionPosition.value = null;
      }

      selectedText.value = { text, cfiRange, contents };
      uiStore.isSelectingText = true;
    });

    load();
  };

  const addHighlight = async (newHighlight: NewAnnotation, profileId?: string) => {
    const text = selectedText.value?.text ?? (await book.value?.getRange(newHighlight.range))?.toString() ?? '';
    const textSample = text.length > 20 ? text.substring(0, 20).trim() + '...' : text.trim();

    // Implement union logic directly where needed or extract if heavily reused
    const overlapping = findOverlappingHighlights(newHighlight.range);

    // Build union range via DOM if there are overlaps; falls back to newRange if page not loaded
    const mergedRange =
      overlapping.length > 0 ? await buildUnionCfi(newHighlight.range, overlapping) : newHighlight.range;

    // Preserve comment from overlapping highlight only when the caller didn't open the note dialog
    // (comment === undefined). When the user explicitly cleared the comment via the dialog,
    // saveWithNote routes to updateHighlight directly — this path is for color-pick saves.
    const comment =
      newHighlight.comment === undefined ? overlapping.find((h) => h.comment)?.comment : newHighlight.comment;

    if (overlapping.length > 0) {
      overlapping.forEach((old) => teardownAnnotation(old));
      for (const extra of overlapping.slice(1)) await remove(extra.id);

      const survivor = overlapping[0];
      const updated = (await update(survivor.id, {
        type: newHighlight.type,
        range: mergedRange,
        color: newHighlight.color,
        comment,
        textSample,
      })) as Highlight;

      const localWithText: HighlightWithText = { ...updated, type: newHighlight.type, text };
      registerHighlight(localWithText);
      renderedAnnotations.set(updated.id, localWithText);
      sendBookReaderEventAnalytics('ANNOTATION_CREATED');
      return;
    }

    const highlight = (await create(
      { ...newHighlight, range: mergedRange, comment, textSample },
      profileId,
    )) as Highlight;
    const localWithText: HighlightWithText = {
      ...highlight,
      type: newHighlight.type || 'highlight',
      color: newHighlight.color,
      text,
    };

    registerHighlight(localWithText);
    renderedAnnotations.set(highlight.id, localWithText);
    sendBookReaderEventAnalytics('ANNOTATION_CREATED');
  };

  const updateHighlight = async (highlight: HighlightWithText) => {
    teardownAnnotation(highlight);
    const textSample =
      highlight.text.length > 20 ? highlight.text.substring(0, 20).trim() + '...' : highlight.text.trim();

    await update(highlight.id, {
      type: highlight.type,
      range: highlight.range,
      color: highlight.color,
      comment: highlight.comment,
      textSample,
    });
    sendBookReaderEventAnalytics('ANNOTATION_UPDATED');
  };

  const removeHighlight = async (highlight: Pick<HighlightWithText, 'id' | 'range' | 'type'>) => {
    await remove(highlight.id);
    teardownAnnotation(highlight);
    sendBookReaderEventAnalytics('ANNOTATION_DELETED');
  };

  const findOverlappingHighlights = (newRange: string): HighlightWithText[] => {
    const newSpine = /^epubcfi\(([^!]+)!/.exec(newRange)?.[1];
    if (!newSpine) return [];
    // Use renderedAnnotations (synchronously updated after every save/update) rather than
    // highlightsWithText (populated by an async watcher) to avoid missing a just-saved
    // annotation when the user re-selects quickly before the watcher resolves.
    return [...renderedAnnotations.values()].filter((h) => {
      const spine = /^epubcfi\(([^!]+)!/.exec(h.range)?.[1];
      return spine === newSpine && cfiRangesOverlap(newRange, h.range);
    });
  };

  const buildUnionCfi = async (newRange: string, overlapping: HighlightWithText[]): Promise<string> => {
    const activeContents = rendition?.getContents() as unknown as Contents[] | undefined;
    if (!activeContents) return newRange;

    for (const c of activeContents) {
      try {
        const allRanges: Range[] = [c.range(newRange), ...overlapping.map((h) => c.range(h.range))];
        const union = allRanges[0].cloneRange();

        for (const r of allRanges.slice(1)) {
          if (r.compareBoundaryPoints(Range.START_TO_START, union) < 0) union.setStart(r.startContainer, r.startOffset);
          if (r.compareBoundaryPoints(Range.END_TO_END, union) > 0) union.setEnd(r.endContainer, r.endOffset);
        }
        return c.cfiFromRange(union);
      } catch {
        /* Attempt next view if CFI fails */
      }
    }
    return newRange;
  };

  const clearSelectedText = () => {
    if (selectedText.value) selectedText.value.contents.window.getSelection()?.removeAllRanges();
    selectedText.value = null;
    selectionPosition.value = null;
    uiStore.isSelectingText = false;
  };

  const addHighlightClickListener = (callback: typeof clickListener) => {
    clickListener = callback;
  };
  const suppressNextSelectionOnce = () => {
    suppressNextSelection.value = true;
    setTimeout(() => {
      suppressNextSelection.value = false;
    }, 150);
  };

  // Resolves CFI, type, and color from a mark element.
  // Fast path: our own data attributes are present.
  // Slow path: epub.js recreated the element on resize, stripping attributes — recover from store.
  const resolveMarkElMeta = (markEl: SVGGElement): MarkElMeta | null => {
    const cfi = markEl.dataset.highlightCfi;
    if (cfi) {
      return {
        cfi,
        type: (markEl.dataset.annotationType as 'highlight' | 'underline') || 'highlight',
        color: markEl.dataset.annotationColor ?? getColor('yellow'),
      };
    }

    // Slow path: recover from epubcfi attribute if epub.js recreated the DOM node
    const epubCfi = markEl.dataset.epubcfi;
    if (!epubCfi) return null;

    const stored = highlightsWithText.value.find((h) => h.range === epubCfi);
    if (!stored) return null;

    const isUnderline = stored.type === 'underline';
    const type = isUnderline ? 'underline' : 'highlight';
    const color = getColor(stored.color);

    // Re-stamp the attributes for next time
    markEl.dataset.annotationId = stored.id;
    markEl.dataset.highlightCfi = stored.range;
    markEl.dataset.annotationType = type;
    markEl.dataset.annotationColor = color;

    return { cfi: epubCfi, type, color };
  };

  /**
   * Called from paginated-reader after each page loads.
   * Re-draws custom rects for ANY user annotation on the displayed page.
   */
  const reapplyAnnotationRects = (contentsDocument: Document): void => {
    // Match ALL annotation groups — both our custom-attributed ones and epub.js default ones
    const groups = contentsDocument.querySelectorAll<SVGGElement>(
      `g.${USER_UNDERLINE_CLASS}, g.${USER_HIGHLIGHT_CLASS}`,
    );

    if (!groups.length || !rendition) return;

    requestAnimationFrame(() => {
      const activeContents = rendition!.getContents() as unknown as Contents[];

      groups.forEach((markEl) => {
        const meta = resolveMarkElMeta(markEl);
        if (!meta) return;

        for (const c of activeContents) {
          try {
            const range = c.range(meta.cfi);
            if (range) {
              applyCustomAnnotationRects(markEl, range, meta.type, meta.color, isDarkTheme.value);
              break;
            }
          } catch {
            // CFI not on this page, safely ignore
          }
        }
      });
    });
  };

  return {
    init,
    highlights: highlightsWithText,
    highlightColors,
    getColor,
    isDarkTheme,
    selectedText,
    clearSelectedText,
    suppressNextSelectionOnce,
    reapplyAnnotationRects,
    reregisterAllHighlights,
    addAnnotation: addHighlight,
    addHighlight,
    updateHighlight,
    removeHighlight,
    addHighlightClickListener,
    findOverlappingHighlights,
    isCfiRangeContainedIn,
    selectionPosition,
    lastUsedAnnotation,
  };
});

export default useHighlightsStore;
